/**
 * The call card's voice waveform: three layered, smooth quadratic-bezier waves on a 2D
 * canvas. It costs a few hundred multiplications per frame and redraws only while the card is
 * in view. Its envelope follows who is speaking: slate for the owner, iris for Vokarta, and a
 * soft idle hum in between.
 *
 * @module
 */
import { clamp, smoothstep } from '@/lib/math'
import { drift, mixRgb, rgba, type Rgb } from './motion'

/** Colours, resolved from the design tokens by the caller. */
export interface WaveColors {
  /** The owner's voice (`--color-fg`, slate). */
  readonly owner: Rgb
  /** Vokarta's voice (`--color-accent`, iris). */
  readonly vokarta: Rgb
}

/** One frame's inputs. */
export interface WaveFrame {
  /** Speaking energy, 0 (idle hum) to 1. */
  readonly level: number
  /** 0 = owner speaking, 1 = Vokarta speaking. */
  readonly tint: number
  /** Seconds since start, for the slow drift. */
  readonly time: number
}

/** Shape of each layer: frequency factor, phase speed, amplitude, alpha, line width. */
const LAYERS = [
  { freq: 1, speed: 2.1, amp: 1, alpha: 0.95, width: 1.75, phase: 0, seed: 1 },
  { freq: 1.55, speed: -1.45, amp: 0.68, alpha: 0.42, width: 1.25, phase: 1.7, seed: 4 },
  { freq: 0.62, speed: 0.95, amp: 0.52, alpha: 0.22, width: 1, phase: 3.1, seed: 7 },
] as const

const POINTS = 72

/** Draws and animates the waveform on one canvas. Call {@link resize} when its box changes. */
export class Waveform {
  private readonly canvas: HTMLCanvasElement
  private readonly context: CanvasRenderingContext2D | null
  private readonly colors: WaveColors
  private readonly phases: number[] = LAYERS.map((layer) => layer.phase)
  private readonly xs = new Float32Array(POINTS + 1)
  private readonly ys = new Float32Array(POINTS + 1)
  private width = 0
  private height = 0

  constructor(canvas: HTMLCanvasElement, colors: WaveColors) {
    this.canvas = canvas
    this.context = canvas.getContext('2d')
    this.colors = colors
  }

  /** Matches the backing store to the CSS box at the device pixel ratio (capped at 2). */
  resize(): void {
    const { width, height } = this.canvas.getBoundingClientRect()
    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    this.width = width
    this.height = height
    this.canvas.width = Math.max(1, Math.round(width * dpr))
    this.canvas.height = Math.max(1, Math.round(height * dpr))
    this.context?.setTransform(dpr, 0, 0, dpr, 0, 0)
  }

  /** Advances the phases by `dt` seconds (faster while someone speaks) and draws. */
  step(dt: number, frame: WaveFrame): void {
    const pace = 0.35 + 0.65 * frame.level
    for (let i = 0; i < LAYERS.length; i++) this.phases[i] += LAYERS[i].speed * pace * dt
    this.draw(frame)
  }

  /** Draws one frame without advancing time (used for the still, reduced-motion wave). */
  draw({ level, tint, time }: WaveFrame): void {
    const ctx = this.context
    const { width: w, height: h } = this
    if (!ctx || w === 0 || h === 0) return
    ctx.clearRect(0, 0, w, h)

    const mid = h / 2
    const half = mid - 2.5
    const voice = mixRgb(this.colors.owner, this.colors.vokarta, tint)
    const audible = smoothstep(0, 0.35, level)
    // A burst envelope at roughly syllable rate, so speech never looks like a clean sine.
    const burst = 0.62 + 0.38 * (0.5 + 0.5 * drift(time * 2.6, 9))
    const amplitude = half * (0.07 + 0.93 * level * burst)
    const cycles = clamp(w / 230, 1.5, 3.4)

    // The baseline: a hairline the voice rides on.
    ctx.lineWidth = 1
    ctx.strokeStyle = rgba(this.colors.owner, 0.07)
    ctx.beginPath()
    ctx.moveTo(0, mid)
    ctx.lineTo(w, mid)
    ctx.stroke()

    for (let layerIndex = LAYERS.length - 1; layerIndex >= 0; layerIndex--) {
      const layer = LAYERS[layerIndex]
      const k = ((Math.PI * 2) / w) * cycles * layer.freq
      const phase = this.phases[layerIndex]
      for (let i = 0; i <= POINTS; i++) {
        const x = (i / POINTS) * w
        const u = i / POINTS
        // Taper to zero at both ends; a slow drift moves the loudest part along the line.
        const taper = Math.sin(Math.PI * u) ** 1.6
        const sway = 0.72 + 0.28 * drift(u * 3.2 + time * 0.55, layer.seed)
        const wave = Math.sin(x * k + phase) * 0.7 + Math.sin(x * k * 2.15 - phase * 1.35) * 0.3
        this.xs[i] = x
        this.ys[i] = mid + amplitude * layer.amp * taper * sway * wave
      }

      // Main layer: a whisper of body between the curve and its mirror.
      if (layerIndex === 0 && audible > 0.01) {
        ctx.beginPath()
        this.trace(ctx, false)
        this.trace(ctx, true)
        ctx.closePath()
        ctx.fillStyle = rgba(voice, 0.07 * audible)
        ctx.fill()
      }

      ctx.beginPath()
      this.trace(ctx, false)
      ctx.lineWidth = layer.width
      ctx.lineCap = 'round'
      ctx.strokeStyle = rgba(voice, layer.alpha * (0.32 + 0.68 * audible))
      ctx.stroke()
    }
  }

  /** Smooth curve through the sampled points (midpoint quadratics). `mirror` runs back flipped. */
  private trace(ctx: CanvasRenderingContext2D, mirror: boolean): void {
    const mid = this.height / 2
    const y = (i: number) => (mirror ? 2 * mid - this.ys[i] : this.ys[i])
    if (!mirror) ctx.moveTo(this.xs[0], y(0))
    else ctx.lineTo(this.xs[POINTS], y(POINTS))
    const from = mirror ? POINTS : 0
    const to = mirror ? 0 : POINTS
    const stepSign = mirror ? -1 : 1
    for (let i = from; i !== to; i += stepSign) {
      const next = i + stepSign
      const cx = (this.xs[i] + this.xs[next]) / 2
      const cy = (y(i) + y(next)) / 2
      ctx.quadraticCurveTo(this.xs[i], y(i), cx, cy)
    }
    ctx.lineTo(this.xs[to], y(to))
  }
}
